import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";

const wait = milliseconds => new Promise(resolve => window.setTimeout(resolve, milliseconds));
const newConversationId = () => window.crypto?.randomUUID?.() || `manual-test-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const plainTextForSpeech = text => String(text || "")
  .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
  .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
  .replace(/```[\s\S]*?```/g, " bloque de código omitido. ")
  .replace(/[`*_>#]/g, "")
  .replace(/^\s*[-+]\s+/gm, "")
  .replace(/^\s*\d+[.)]\s+/gm, "")
  .replace(/\n+/g, ". ")
  .trim();

// Las palabras del nombre y de la descripción son pistas, no una interpretación de la imagen.
const imageWords = text => String(text || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  .replace(/\.[a-z0-9]{2,5}$/i, "").split(/[^a-z0-9]+/).filter(word => word.length >= 3);
const commonWords = new Set(["como", "cual", "cuales", "para", "por", "con", "del", "las", "los", "una", "unos", "unas", "que", "ese", "esta", "este", "eso", "imagen", "imagenes", "captura", "capturas", "foto", "fotos", "pantalla", "pantallas", "muestra", "mostrar", "manual", "paso", "pasos", "puedo", "quiero", "necesito", "donde", "tengo", "favor"]);
export const selectRelevantImages = (question, images) => {
  const terms = imageWords(question).filter(word => !commonWords.has(word));
  if (!terms.length) return [];
  return images.filter(image => {
    const words = new Set([...imageWords(image.file_name || image.fileName), ...imageWords(image.caption)]);
    return terms.some(term => words.has(term));
  });
};
const requestsImages = question => /\b(imagen|imagenes|captura|capturas|foto|fotos)\b/.test(
  String(question || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
);
export const imagesForQuestion = (question, images) => {
  const matched = selectRelevantImages(question, images);
  return matched.length ? matched : requestsImages(question) ? images : [];
};

export default function AgentKnowledgeTestChat({ manual, images = [], onStartJob, onGetJob, onCloseSession, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [manualSpeaking, setManualSpeaking] = useState(false);
  const [error, setError] = useState("");
  const conversationId = useRef(newConversationId());
  const started = useRef(false);
  const bottomRef = useRef(null);
  const mounted = useRef(false);
  const closed = useRef(false);
  const closingRef = useRef(false);

  const stopManualSpeech = useCallback(() => {
    window.speechSynthesis?.cancel();
    setManualSpeaking(false);
  }, []);

  const speakManual = () => {
    if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance !== "function") {
      setError("Este navegador no tiene disponible la lectura de voz.");
      return;
    }
    const text = plainTextForSpeech(`${manual.title}. ${manual.content_markdown}`);
    if (!text) return;

    stopManualSpeech();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = "es-MX";
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.onstart = () => setManualSpeaking(true);
    utterance.onend = () => setManualSpeaking(false);
    utterance.onerror = () => setManualSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.speechSynthesis?.cancel();
      const cleanup = async (attempt) => {
        if (mounted.current || closed.current || !started.current) return;
        if (closingRef.current) {
          if (attempt < 24) window.setTimeout(() => cleanup(attempt + 1), 5000);
          return;
        }
        try {
          await onCloseSession(conversationId.current);
          closed.current = true;
        } catch {
          if (attempt < 24) window.setTimeout(() => cleanup(attempt + 1), 5000);
        }
      };
      window.setTimeout(() => cleanup(0), 0);
    };
  }, [onCloseSession]);

  const closeSession = async () => {
    if (busy || closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    setError("");
    try {
      await onCloseSession(conversationId.current);
      closed.current = true;
      stopManualSpeech();
      if (mounted.current) onClose();
    } catch (closeError) {
      if (mounted.current) setError(closeError.message || "No se pudo confirmar el borrado de la conversación. Inténtalo de nuevo.");
    } finally {
      closingRef.current = false;
      if (mounted.current) setClosing(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const waitForAnswer = useCallback(async jobId => {
    for (let attempt = 0; attempt < 300; attempt += 1) {
      await wait(2000);
      if (!mounted.current) return null;
      const result = await onGetJob(jobId);
      if (!mounted.current) return null;
      if (result?.status === "completed" || result?.status === "failed") return result;
    }
    throw new Error("La respuesta está tardando más de lo esperado. Puedes volver a consultar en un momento.");
  }, [onGetJob]);

  const sendMessage = useCallback(async (text = draft) => {
    const question = String(text || "").trim();
    if (!question || busy) return;
    setBusy(true);
    setError("");
    setDraft("");
    const userKey = newConversationId();
    const assistantKey = newConversationId();
    setMessages(current => [
      ...current,
      { id: userKey, role: "user", content: question },
      { id: assistantKey, role: "assistant", content: "Estoy revisando este manual…", pending: true, question },
    ]);
    try {
      const job = await onStartJob(manual.manual_id, {
        conversation_id: conversationId.current,
        message: question,
      });
      if (!mounted.current) return;
      const jobId = job?.jobId ?? job?.job_id;
      if (!jobId) throw new Error("n8n no devolvió el identificador del trabajo de prueba.");
      setMessages(current => current.map(message => message.id === assistantKey
        ? { ...message, content: job?.waitingResponse || job?.waiting_response || message.content }
        : message));
      const result = await waitForAnswer(jobId);
      if (!result || !mounted.current) return;
      if (result.status === "failed") throw new Error(result.answer || "El agente no pudo completar la prueba.");
      setMessages(current => current.map(message => message.id === assistantKey
        ? { ...message, content: result.answer || result.response || result.message || "El agente no devolvió texto.", sources: result.sources || [], pending: false }
        : message));
    } catch (requestError) {
      if (!mounted.current) return;
      setMessages(current => current.map(message => message.id === assistantKey
        ? { ...message, content: "", pending: false, error: requestError.message }
        : message));
      setError(requestError.message || "No se pudo consultar al agente.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [busy, draft, manual?.manual_id, onStartJob, waitForAnswer]);

  useEffect(() => {
    if (started.current || !manual?.manual_id) return;
    started.current = true;
    const topic = manual.topic || manual.title || "este manual";
    sendMessage(`Describe qué información contiene el manual sobre ${topic}. Si no hay pasos o detalles suficientes, indícalo.`);
  }, [manual?.manual_id, manual?.title, manual?.topic, sendMessage]);

  const firstAnswerId = messages.find(message => message.role === "assistant" && !message.pending && !message.error)?.id;
  const availableImages = images.filter(image => image?.url);
  const expectedImageCount = Math.max(Number(manual.image_count) || 0, images.length);

  return <Card variant="outlined" sx={{ mt: 2 }}>
    <CardContent>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
        <Box>
          <Typography variant="h6">Prueba de conocimiento con el agente</Typography>
          <Typography variant="body2" color="text.secondary">Manual: {manual.title} · {manual.topic} · {manual.is_published ? "publicado" : "borrador"}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
          <Button type="button" variant="outlined" startIcon={manualSpeaking ? <StopIcon /> : <PlayArrowIcon />}
            onClick={manualSpeaking ? stopManualSpeech : speakManual}>
            {manualSpeaking ? "Detener lectura" : "Escuchar manual"}
          </Button>
          <Button type="button" disabled={busy || closing} onClick={closeSession}>{closing ? "Cerrando…" : "Cerrar y borrar conversación"}</Button>
        </Stack>
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      {expectedImageCount > availableImages.length && <Alert severity="warning" sx={{ mb: 1.5 }}>No se pudieron cargar todas las imágenes guardadas en este manual.</Alert>}
      <Box role="log" aria-live="polite" sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 420, overflowY: "auto", p: 0.5 }}>
        {!messages.length && <Typography color="text.secondary">Preparando la pregunta de prueba…</Typography>}
        {messages.map(message => <Stack key={message.id} direction="row" spacing={1} alignItems="flex-start" sx={{ alignSelf: message.role === "user" ? "flex-end" : "stretch", maxWidth: { xs: "100%", md: "88%" } }}>
          {message.role === "assistant" && <Avatar sx={{ width: 30, height: 30, bgcolor: "primary.main", fontSize: 14 }}>AI</Avatar>}
          <Box sx={{ bgcolor: message.role === "user" ? "action.selected" : "background.default", border: "1px solid", borderColor: "divider", borderRadius: 2, px: 1.5, py: 1, minWidth: 120 }}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{message.content}</Typography>
            {message.pending && <CircularProgress size={18} sx={{ mt: 1 }} />}
            {message.error && <Alert severity="error" sx={{ mt: 1 }}>{message.error}</Alert>}
            {message.role === "assistant" && !message.pending && !message.error && !!availableImages.length && (() => {
              const matched = selectRelevantImages(message.question, availableImages);
              const showAll = message.id === firstAnswerId || (requestsImages(message.question) && !matched.length);
              const selected = message.id === firstAnswerId ? availableImages : imagesForQuestion(message.question, availableImages);
              return !!selected.length && <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{showAll ? "Imágenes guardadas en el manual" : "Imágenes relacionadas por nombre o descripción"}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 1 }}>
                {selected.map((image, index) => <Box key={`${image.image_id || image.file_name || index}`}>
                  <Box component="a" href={image.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir ${image.caption || image.file_name || `imagen ${index + 1}`}`}>
                    <Box component="img" src={image.url} alt={image.caption || image.file_name || `Imagen ${index + 1} del manual`} sx={{ width: { xs: 110, sm: 150 }, height: 100, objectFit: "contain", border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }} />
                  </Box>
                  {image.caption && <Typography variant="caption" display="block" sx={{ maxWidth: 150 }}>{image.caption}</Typography>}
                </Box>)}
              </Stack>
            </Box>;
            })()}
          </Box>
          {message.role === "user" && <Avatar sx={{ width: 30, height: 30, bgcolor: "secondary.main", fontSize: 13 }}>Tú</Avatar>}
        </Stack>)}
        <div ref={bottomRef} />
      </Box>
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      <Box component="form" onSubmit={event => { event.preventDefault(); sendMessage(); }} sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "flex-end" }}>
        <TextField fullWidth label="Pregunta sobre este manual" value={draft} onChange={event => setDraft(event.target.value)} multiline maxRows={4} disabled={busy || closing} inputProps={{ maxLength: 8000 }} />
        <Button type="submit" variant="contained" disabled={busy || closing || !draft.trim()}>{busy ? "Procesando…" : "Enviar"}</Button>
      </Box>
    </CardContent>
  </Card>;
}
